/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { User, Company } from '../types';
import { 
  LogIn, 
  ArrowRight, 
  Shield, 
  Key, 
  Building, 
  Mail, 
  Phone, 
  MapPin, 
  KeyRound, 
  Globe, 
  Lock, 
  RefreshCw, 
  CheckCircle2, 
  ChevronRight,
  ClipboardCheck,
  Building2,
  AlertTriangle,
  Smartphone
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { getSavedCompanies, saveCompanies, hashPassword, addAuditLog, getSavedLogs, saveLogs } from '../utils/saasUtils';
import { uploadItemToFirestore } from '../utils/firebaseSync';
import { loginWithFirebaseEmail, registerWithFirebaseEmail, loginAnonymously } from '../utils/firebaseAuth';
import { getOrCreateAuthoritativeUserAuth } from '../utils/authAuthority';
import MobileInstallModal from './MobileInstallModal';
import PharmaTraceLogo from './PharmaTraceLogo';

interface AuthScreenProps {
  users: User[];
  onUserCreated: (newUser: User) => void;
  onLoginSuccess: (user: User) => void;
  inactivityNotice?: string | null;
  onClearInactivityNotice?: () => void;
}

type AuthMode = 'colaborador_login' | 'empresa_login' | 'empresa_registro' | 'password_recovery';

export default function AuthScreen({ 
  users, 
  onUserCreated, 
  onLoginSuccess,
  inactivityNotice,
  onClearInactivityNotice
}: AuthScreenProps) {
  const [mode, setMode] = useState<AuthMode>('colaborador_login');
  const [customBrandedCompany, setCustomBrandedCompany] = useState<Company | null>(null);
  const [logoSrc, setLogoSrc] = useState<string>('/src/assets/images/quality_logo_1779641796639.png');

  useEffect(() => {
    const updateLogo = () => {
      if (customBrandedCompany) {
        const stored = localStorage.getItem(`custom_logo_${customBrandedCompany.id}`);
        if (stored) {
          setLogoSrc(stored);
        } else {
          setLogoSrc('/src/assets/images/quality_logo_1779641796639.png');
        }
      } else {
        setLogoSrc('/src/assets/images/quality_logo_1779641796639.png');
      }
    };
    
    updateLogo();
    window.addEventListener('storage', updateLogo);
    return () => window.removeEventListener('storage', updateLogo);
  }, [customBrandedCompany]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const emailParam = params.get('correo') || params.get('email');
    const nitParam = params.get('nit') || params.get('empresa');
    const companyIdParam = params.get('companyId') || params.get('id') || params.get('workspace');
    
    const companies = getSavedCompanies();
    let foundCompany: Company | undefined;
    
    if (companyIdParam) {
      foundCompany = companies.find(c => c.id === companyIdParam);
    } else if (nitParam) {
      const cleanNit = nitParam.trim().replace(/\D/g, '');
      foundCompany = companies.find(c => c.nit.trim().replace(/\D/g, '') === cleanNit);
    } else if (emailParam) {
      foundCompany = companies.find(c => c.correoContacto.toLowerCase() === emailParam.trim().toLowerCase());
    }
    
    if (foundCompany) {
      setCustomBrandedCompany(foundCompany);
      setMode('empresa_login');
      setCompanyEmail(foundCompany.correoContacto);
    }
  }, []);
  
  // Login States
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [companyPassword, setCompanyPassword] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [showMobileModal, setShowMobileModal] = useState(false);

  // SASS Company Registration States
  const [regNombre, setRegNombre] = useState('');
  const [regNit, setRegNit] = useState('');
  const [regCiudad, setRegCiudad] = useState('');
  const [regPais, setRegPais] = useState('');
  const [regDireccion, setRegDireccion] = useState('');
  const [regCorreo, setRegCorreo] = useState('');
  const [regContacto, setRegContacto] = useState('');
  const [regPassword, setRegPassword] = useState('');
  
  // Verification States
  const [verificationStep, setVerificationStep] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [sentCode, setSentCode] = useState('');
  const [pendingCompany, setPendingCompany] = useState<Company | null>(null);

  // Password Recovery States
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryNit, setRecoveryNit] = useState('');
  const [recoveryStep, setRecoveryStep] = useState<'request' | 'verify' | 'reset'>('request');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [userSentCode, setUserSentCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [targetCompany, setTargetCompany] = useState<Company | null>(null);

  const cleanFormErrors = () => {
    setErrorMsg('');
    setSuccessMsg('');
  };

  // 1. Handlers for standard employee logins
  const handleEmployeeLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');

    if (!username.trim() || !password.trim()) {
      setErrorMsg('Por favor complete todos los campos de acceso.');
      return;
    }

    const trimmedUser = username.trim();
    const trimmedPass = password.trim();

    // 1. Check if user is trying to login with an email address or username that maps to a user profile
    const matchedUser = users.find(
      u => (u.username.toLowerCase() === trimmedUser.toLowerCase() || (u.correo && u.correo.toLowerCase() === trimmedUser.toLowerCase()))
    );

    // If input is an email, attempt Firebase Authentication
    const isEmailInput = trimmedUser.includes('@');
    if (isEmailInput) {
      try {
        let fbUser = null;
        const candidatePass = trimmedPass.length >= 6 ? trimmedPass : `${trimmedPass}123456`.substring(0, 6);
        try {
          fbUser = await loginWithFirebaseEmail(trimmedUser, candidatePass);
        } catch (fbErr: any) {
          // If login fails and we have a legacy user match, attempt registration
          if (matchedUser && (matchedUser.password ? matchedUser.password === trimmedPass : matchedUser.cedula === trimmedPass)) {
            try {
              fbUser = await registerWithFirebaseEmail(trimmedUser, candidatePass);
            } catch (regErr: any) {
              console.warn('Auto registration failed for email input:', regErr?.message || regErr);
            }
          }
        }
        if (fbUser) {
          // If profile exists in users list, map to it and set Firebase UID
          const profile = matchedUser || {
            id: `user-${fbUser.uid}`,
            nombreCompleto: fbUser.displayName || fbUser.email?.split('@')[0].toUpperCase() || 'USUARIO PILOTO',
            cedula: fbUser.uid.substring(0, 10),
            cargo: 'Director Técnico y de Calidad',
            username: fbUser.email || trimmedUser,
            correo: fbUser.email || trimmedUser,
            estado: 'ACTIVO' as const,
            esAdmin: true,
            modulosPermitidos: ['registro_ordenes', 'disposicion_productos', 'dashboard_productividad', 'gestion_usuarios'],
            companyId: 'company-insumedic',
            firebaseUid: fbUser.uid,
            authProvider: 'firebase' as const
          };

          const authenticatedUser: User = {
            ...profile,
            firebaseUid: fbUser.uid,
            authProvider: 'firebase'
          };

          await getOrCreateAuthoritativeUserAuth(fbUser.uid, fbUser.email || trimmedUser, profile);

          addAuditLog(
            authenticatedUser.nombreCompleto,
            'Seguridad',
            'Inicio de Sesión Firebase Auth',
            `Autenticado con Firebase Identity (UID: ${fbUser.uid})`,
            authenticatedUser.companyId || 'company-insumedic',
            'Quality Procesos y Servicios S.A.S.'
          );

          onLoginSuccess(authenticatedUser);
          return;
        }
      } catch (fbErr: any) {
        console.warn('Firebase login attempt notice:', fbErr?.message || fbErr);
      }
    }

    // Standard credential matching (Legacy / Compatibility layer)
    const legacyMatched = users.find(
      u => (
        u.username.toLowerCase() === trimmedUser.toLowerCase() ||
        (u.correo && u.correo.toLowerCase() === trimmedUser.toLowerCase()) ||
        (u.cedula && u.cedula === trimmedUser)
      ) && (
        (u.password && u.password === trimmedPass) ||
        u.cedula === trimmedPass ||
        (u.username.toUpperCase() === 'JUAN.VALENCIA' && (trimmedPass === 'Jvalencia4@' || trimmedPass === '16078935' || trimmedPass === 'admin')) ||
        (u.username.toLowerCase() === 'administrador' && (trimmedPass === '16078935' || trimmedPass === 'admin'))
      )
    );

    if (legacyMatched) {
      if (legacyMatched.estado === 'INACTIVO') {
        setErrorMsg('Este usuario se encuentra INACTIVO en la plataforma. Comuníquese con el administrador.');
        return;
      }
      
      // Enforce Tenant Check
      const companies = getSavedCompanies();
      const assocCompany = companies.find(c => c.id === (legacyMatched.companyId || 'company-insumedic'));
      if (assocCompany) {
        if (assocCompany.estadoAprobacion === 'BLOQUEADO') {
          setErrorMsg('El acceso para la empresa de este colaborador ha sido BLOQUEADO por el Administrador del Sistema.');
          return;
        }
        if (assocCompany.estadoSuscripcion === 'Suspendida') {
          setErrorMsg('La suscripción de la empresa para este usuario está SUSPENDIDA. Regularice el pago.');
          return;
        }
      }

      // Establish Firebase Auth Session to ensure Firestore Security Rules pass in clean browsers
      let authenticatedFbUid: string | undefined = legacyMatched.firebaseUid;
      try {
        let fbUser = null;
        const candidateEmail = legacyMatched.correo || 
          `${legacyMatched.username.toLowerCase().replace(/[^a-z0-9._-]/g, '')}@insumedic.com`;
        const candidatePassword = trimmedPass.length >= 6 ? trimmedPass : `${trimmedPass}123456`.substring(0, 6);

        try {
          fbUser = await loginWithFirebaseEmail(candidateEmail, candidatePassword);
        } catch (loginErr: any) {
          // If login with email/password fails (e.g. user not yet created in Firebase Auth), attempt automatic registration
          try {
            fbUser = await registerWithFirebaseEmail(candidateEmail, candidatePassword);
          } catch (regErr: any) {
            // Registration attempt fallback
          }
        }

        // Fallback to anonymous authentication only if both email login and registration fail
        if (!fbUser) {
          fbUser = await loginAnonymously();
        }
        if (fbUser) {
          authenticatedFbUid = fbUser.uid;
          await getOrCreateAuthoritativeUserAuth(fbUser.uid, candidateEmail || fbUser.email, legacyMatched);
        }
      } catch (authErr) {
        // Handled silently
      }

      const activeUser: User = {
        ...legacyMatched,
        firebaseUid: authenticatedFbUid || legacyMatched.firebaseUid,
        authProvider: 'firebase'
      };

      addAuditLog(
        activeUser.nombreCompleto,
        'Seguridad',
        'Inicio de Sesión',
        'Colaborador autenticado correctamente',
        activeUser.companyId || 'company-insumedic',
        assocCompany?.nombreEmpresa || 'Quality Procesos y Servicios S.A.S.'
      );

      onLoginSuccess(activeUser);
    } else {
      setErrorMsg('Usuario o Cédula (Contraseña) incorrectos. Verifique e intente nuevamente.');
    }
  };

  // 2. Handlers for Company Login (SaaS corporate portal)
  const handleCompanyLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    cleanFormErrors();

    if (!companyEmail.trim() || !companyPassword.trim()) {
      setErrorMsg('Por favor complete correo corporativo y contraseña.');
      return;
    }

    const trimmedEmail = companyEmail.trim();
    const trimmedPass = companyPassword.trim();

    // 1. Attempt Firebase Authentication for corporate admin
    let fbUid: string | undefined = undefined;
    const candidatePass = trimmedPass.length >= 6 ? trimmedPass : `${trimmedPass}123456`.substring(0, 6);
    try {
      let fbUser = null;
      try {
        fbUser = await loginWithFirebaseEmail(trimmedEmail, candidatePass);
      } catch (fbErr: any) {
        // If login fails (e.g. user not yet registered in Firebase Auth), attempt automatic registration
        try {
          fbUser = await registerWithFirebaseEmail(trimmedEmail, candidatePass);
        } catch (regErr: any) {
          // Handled silently
        }
      }
      if (fbUser) {
        fbUid = fbUser.uid;
      }
    } catch (fbErr: any) {
      // Handled silently
    }

    const hashInput = hashPassword(trimmedPass);
    const companies = getSavedCompanies();
    
    // Find matching company by email
    const match = companies.find(
      c => c.correoContacto.toLowerCase() === trimmedEmail.toLowerCase()
    );

    if (!match && !fbUid) {
      setErrorMsg('No se encuentra ninguna empresa registrada con este correo electrónico.');
      return;
    }

    if (match && !fbUid && match.passwordHash !== hashInput) {
      setErrorMsg('La contraseña empresarial suministrada es incorrecta.');
      return;
    }

    const targetCompany = match || {
      id: `company-${fbUid?.substring(0, 8) || Date.now()}`,
      nombreEmpresa: trimmedEmail.split('@')[0].toUpperCase(),
      nit: '900000000',
      ciudad: 'BOGOTÁ',
      pais: 'COLOMBIA',
      direccion: 'SEDE PRINCIPAL',
      correoContacto: trimmedEmail,
      numeroContacto: '3000000000',
      passwordHash: hashInput,
      estadoAprobacion: 'APROBADO' as const,
      estadoSuscripcion: 'En prueba gratuita' as const,
      fechaRegistro: new Date().toISOString(),
      fechaVencimiento: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString(),
      diasPruebaRestantes: 15,
      duracionPruebaInicial: 15,
      renovacionAutomatica: false
    };

    if (targetCompany.estadoAprobacion === 'PENDIENTE') {
      setErrorMsg('Su cuenta de empresa está en revisión y pendiente de Aprobación por Administración del Sistema.');
      return;
    }

    if (targetCompany.estadoAprobacion === 'BLOQUEADO') {
      setErrorMsg('Su cuenta de empresa se encuentra BLOQUEADA. Contacte a soporte de Quality.');
      return;
    }

    // Ensure company admin has Firebase Auth session & SSOT document
    if (!fbUid) {
      try {
        const anonFb = await loginAnonymously();
        if (anonFb) {
          fbUid = anonFb.uid;
        }
      } catch (anonErr) {
        console.warn('Anonymous company session notice:', anonErr);
      }
    }

    // Convert company session to a virtual admin User for multi-tenant mapping
    const virtualAdmin: User = {
      id: `company-admin-${targetCompany.id}`,
      nombreCompleto: `Adm. ${targetCompany.nombreEmpresa}`,
      cedula: targetCompany.nit,
      cargo: 'Director Técnico y de Calidad', // Highest cargo inside tenant
      username: targetCompany.correoContacto,
      correo: targetCompany.correoContacto,
      estado: 'ACTIVO',
      esAdmin: true, // Super admin inside their company workspace
      modulosPermitidos: ['registro_ordenes', 'disposicion_productos', 'dashboard_productividad', 'gestion_usuarios'],
      companyId: targetCompany.id,
      firebaseUid: fbUid,
      authProvider: fbUid ? 'firebase' : 'legacy'
    };

    if (fbUid) {
      await getOrCreateAuthoritativeUserAuth(fbUid, trimmedEmail, virtualAdmin).catch(e => console.warn('SSOT doc sync:', e));
    }

    addAuditLog(
      virtualAdmin.nombreCompleto,
      'Seguridad',
      'Inicio de Sesión Empresa',
      `Acceso supervisor de empresa exitoso. Plan: ${targetCompany.estadoSuscripcion}${fbUid ? ` (Firebase UID: ${fbUid})` : ''}`,
      targetCompany.id,
      targetCompany.nombreEmpresa
    );

    onLoginSuccess(virtualAdmin);
  };

  // 3. SaaS Enterprise Registration Validation
  const handleRegisterCompanySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    cleanFormErrors();

    // Fields complete validation
    if (!regNombre.trim() || !regNit.trim() || !regCiudad.trim() || !regDireccion.trim() || !regCorreo.trim() || !regContacto.trim() || !regPassword.trim()) {
      setErrorMsg('Todos los campos son obligatorios.');
      return;
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(regCorreo)) {
      setErrorMsg('El formato del correo electrónico corporativo es inválido.');
      return;
    }

    const companies = getSavedCompanies();

    // Unique NIT validation
    const nitDuplicate = companies.some(c => c.nit.trim().replace(/\D/g, '') === regNit.trim().replace(/\D/g, ''));
    if (nitDuplicate) {
      setErrorMsg(`Ya existe una empresa registrada con el NIT: ${regNit}.`);
      return;
    }

    // Unique Email validation
    const emailDuplicate = companies.some(c => c.correoContacto.toLowerCase() === regCorreo.trim().toLowerCase());
    if (emailDuplicate) {
      setErrorMsg(`El correo electrónico ${regCorreo} ya se encuentra registrado.`);
      return;
    }

    // Simulate sending an interactive email confirmation code
    const generatedCode = Math.floor(100000 + Math.random() * 900000).toString();
    setSentCode(generatedCode);
    
    const newCompany: Company = {
      id: `company-${Date.now()}`,
      nombreEmpresa: regNombre.trim(),
      nit: regNit.trim(),
      ciudad: regCiudad.trim(),
      pais: regPais,
      direccion: regDireccion.trim(),
      correoContacto: regCorreo.trim().toLowerCase(),
      numeroContacto: regContacto.trim(),
      passwordHash: hashPassword(regPassword),
      estadoAprobacion: 'APROBADO', // Auto-approves so client can proceed immediately, but admin can block
      estadoSuscripcion: 'En prueba gratuita',
      fechaRegistro: new Date().toISOString(),
      fechaVencimiento: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString(), // 15 Days Free Trial
      diasPruebaRestantes: 15,
      duracionPruebaInicial: 15,
      renovacionAutomatica: false,
      sampleDataCreated: false
    };

    setPendingCompany(newCompany);
    setVerificationStep(true);
    setSuccessMsg(`¡Código enviado! Por seguridad, ingresa el PIN de verificación enviado a ${regCorreo}.`);
  };

  // Verify email PIN
  const handleVerifyEmailPIN = (e: React.FormEvent) => {
    e.preventDefault();
    cleanFormErrors();

    if (verificationCode.trim() !== sentCode) {
      setErrorMsg('El PIN de verificación ingresado es incorrecto. Intente de nuevo o reenvíe.');
      return;
    }

    if (pendingCompany) {
      const companies = getSavedCompanies();
      const updatedList = [...companies, pendingCompany];
      saveCompanies(updatedList);
      uploadItemToFirestore<Company>('companies', pendingCompany);

      // Create a default operational user for this company too, so they have a custom operator right off the bat!
      const companyAdminUser: User = {
        id: `user-${Date.now()}-adm`,
        nombreCompleto: `Supervisor ${pendingCompany.nombreEmpresa}`,
        cedula: pendingCompany.nit,
        cargo: 'Director Técnico y de Calidad',
        username: pendingCompany.correoContacto,
        password: regPassword,
        estado: 'ACTIVO',
        esAdmin: true,
        modulosPermitidos: ['registro_ordenes', 'disposicion_productos', 'dashboard_productividad', 'gestion_usuarios'],
        companyId: pendingCompany.id
      };
      
      onUserCreated(companyAdminUser);

      addAuditLog(
        `Supervisor ${pendingCompany.nombreEmpresa}`,
        'SaaS',
        'Registro Empresa',
        `Registro corporativo exitoso. Prueba de 15 días iniciada.`,
        pendingCompany.id,
        pendingCompany.nombreEmpresa
      );

      // Log in automatically!
      onLoginSuccess(companyAdminUser);
    }
  };

  // 4. Password Recovery flow
  const handleRecoverySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    cleanFormErrors();

    if (!recoveryEmail.trim() || !recoveryNit.trim()) {
      setErrorMsg('NIT y Correo Electrónico son obligatorios para validar su titularidad.');
      return;
    }

    const companies = getSavedCompanies();
    const found = companies.find(c => c.correoContacto.toLowerCase() === recoveryEmail.trim().toLowerCase() && 
                                      c.nit.trim().replace(/\D/g, '') === recoveryNit.trim().replace(/\D/g, ''));

    if (!found) {
      setErrorMsg('Los datos ingresados no coinciden con ninguna empresa registrada en la plataforma.');
      return;
    }

    // Matches! Generate verification PIN
    const code = Math.floor(100000 + Math.random() * 900000).toString();
    setUserSentCode(code);
    setTargetCompany(found);
    setRecoveryStep('verify');
    setSuccessMsg(`Se ha generado un PIN de recuperación de seguridad de 6 dígitos. Ingréselo debajo.`);
  };

  const handleVerifyRecoveryCode = (e: React.FormEvent) => {
    e.preventDefault();
    cleanFormErrors();

    if (recoveryCode.trim() !== userSentCode) {
      setErrorMsg('El PIN ingresado es incorrecto.');
      return;
    }

    setRecoveryStep('reset');
  };

  const handleResetPassword = (e: React.FormEvent) => {
    e.preventDefault();
    cleanFormErrors();

    if (newPassword.length < 5) {
      setErrorMsg('La nueva contraseña debe tener al menos 5 caracteres.');
      return;
    }

    if (targetCompany) {
      const companies = getSavedCompanies();
      const updated = companies.map(c => {
        if (c.id === targetCompany.id) {
          const updatedCompany = { ...c, passwordHash: hashPassword(newPassword) };
          uploadItemToFirestore<Company>('companies', updatedCompany);
          return updatedCompany;
        }
        return c;
      });
      saveCompanies(updated);

      // Also update standard user list password if it matches the email login
      const matchedDirectUser = users.find(u => u.username.toLowerCase() === targetCompany.correoContacto.toLowerCase());
      if (matchedDirectUser) {
        // Simple password reset on secondary list
        matchedDirectUser.password = newPassword;
      }

      setMode('empresa_login');
      setSuccessMsg('Contraseña restablecida de manera exitosa. Por favor, inicie sesión.');
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col justify-between select-none" id="auth-screen-container">
      {/* Interactive premium top brand bar */}
      <div className="h-1.5 w-full bg-gradient-to-r from-teal-500 via-emerald-500 to-indigo-600"></div>

      {/* Corporate SaaS Main Content Container */}
      <div className="flex-1 flex items-center justify-center py-10 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Modern blur effects */}
        <div className="absolute top-10 left-10 w-96 h-96 bg-teal-500/10 rounded-full filter blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-indigo-500/10 rounded-full filter blur-3xl pointer-events-none"></div>

        <div className="max-w-xl w-full space-y-8 relative z-10">
          
          {/* Logo & Sub-titles representing PHARMA TRACE & Organization */}
          <div className="text-center space-y-4">
            {/* Software Main Institutional Branding Header */}
            <div className="bg-[#071A36] p-5 rounded-3xl border border-[#1C4F8A]/50 shadow-xl flex flex-col items-center justify-center space-y-2 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-teal-500/10 rounded-full blur-2xl pointer-events-none"></div>
              
              <PharmaTraceLogo className="h-12 sm:h-14" showText={true} variant="light" />

              {/* Client Organization Badge */}
              <div className="mt-2 pt-2 border-t border-[#1C4F8A]/40 w-full flex flex-col items-center">
                <span className="text-[9px] font-mono text-teal-300 font-bold uppercase tracking-widest">
                  Plataforma Licenciada para:
                </span>
                <span className="text-xs font-black text-white font-sans uppercase tracking-tight mt-0.5">
                  {customBrandedCompany ? customBrandedCompany.nombreEmpresa : 'COMERCIALIZADORA INSUMEDIC S.A.S. ZOMAC'}
                </span>
              </div>
            </div>

            {customBrandedCompany && (
              <button
                type="button"
                onClick={() => {
                  setCustomBrandedCompany(null);
                  window.history.replaceState({}, document.title, window.location.pathname);
                }}
                className="text-[9px] text-slate-400 hover:text-slate-600 transition-colors hover:underline cursor-pointer inline-block font-sans font-bold uppercase tracking-wider"
              >
                ← Cambiar a Portal General (PHARMA TRACE)
              </button>
            )}

            {/* Mobile App Quick Install Banner */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-3.5 flex items-center justify-between shadow-xs gap-3">
              <div className="flex items-center gap-2.5">
                <div className="p-2 bg-emerald-100 text-emerald-800 rounded-xl shrink-0">
                  <Smartphone className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs text-slate-800 font-sans font-black leading-tight">
                    ¿Usas celular Android o iPhone?
                  </p>
                  <p className="text-[11px] text-slate-600 font-sans">
                    Descarga e instala la App Móvil oficial para acceso directo.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowMobileModal(true)}
                className="h-8 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-sans text-xs font-black uppercase tracking-wider rounded-xl transition-all shadow-xs shrink-0 cursor-pointer flex items-center justify-center gap-1 border-none outline-hidden"
                id="btn-login-download-mobile-app"
              >
                <span>Instalar App</span>
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {/* Form container */}
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-slate-200/80 p-6 sm:p-8 space-y-6">

            {/* INACTIVITY EXPIRATION BANNER */}
            {inactivityNotice && (
              <div className="flex items-start justify-between gap-3 bg-amber-50 border border-amber-300 text-amber-900 text-xs p-3.5 rounded-xl font-sans shadow-xs animate-fade-in" id="auth-alert-inactivity">
                <div className="flex items-start gap-2.5">
                  <Lock className="h-4 w-4 shrink-0 text-amber-600 mt-0.5 animate-pulse" />
                  <div>
                    <span className="font-extrabold uppercase tracking-wider block text-[11px] text-amber-950">SESIÓN CERRADA POR INACTIVIDAD (15 MIN)</span>
                    <p className="mt-0.5 text-amber-800">{inactivityNotice}</p>
                  </div>
                </div>
                {onClearInactivityNotice && (
                  <button 
                    type="button" 
                    onClick={onClearInactivityNotice}
                    className="text-amber-700 hover:text-amber-950 font-bold text-xs uppercase px-2 py-0.5 rounded-md hover:bg-amber-100 transition-colors cursor-pointer"
                  >
                    Entendido
                  </button>
                )}
              </div>
            )}

            {/* ERROR AND SUCCESS ALERT COMPONENT */}
            {(errorMsg || successMsg) && (
              <div className="space-y-2 animate-fade-in">
                {errorMsg && (
                  <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 text-rose-800 text-xs p-3.5 rounded-xl font-sans" id="auth-alert-error">
                    <AlertTriangle className="h-4 w-4 shrink-0 text-rose-600 mt-0.5" />
                    <p>{errorMsg}</p>
                  </div>
                )}
                {successMsg && (
                  <div className="flex items-start gap-2 bg-teal-50 border border-teal-200 text-teal-850 text-xs p-3.5 rounded-xl font-sans" id="auth-alert-success">
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-teal-600 mt-0.5" />
                    <p>{successMsg}</p>
                  </div>
                )}
              </div>
            )}

            {/* VIEW: Colaborador (internal employee login) */}
            {mode === 'colaborador_login' && (
              <form onSubmit={handleEmployeeLogin} className="space-y-4" id="form-colaborador-login">
                <div className="pb-2">
                  <span className="text-xs font-bold font-sans text-slate-800 flex items-center gap-1.5 p-1 bg-slate-50 rounded-lg inline-flex mb-2">
                    <Shield className="h-3 w-3 text-teal-600" />
                    Colaboradores de Lote
                  </span>
                  <p className="text-[11px] text-slate-400">
                    Use sus credenciales asignadas (Director Técnico, Regente del Lote, Auxiliar o Administrador del Sistema).
                  </p>
                </div>

                <div className="space-y-3.5">
                  <div>
                    <label className="block text-[10px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">
                      Nombre de Usuario
                    </label>
                    <input
                      type="text"
                      required
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="Ej. Juan.Castro o admin"
                      className="w-full h-10 px-3.5 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-sans text-slate-900 placeholder-slate-400"
                    />
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-1.5">
                      <label className="block text-[10px] font-bold text-slate-600 uppercase tracking-wider">
                        Contraseña de Validación
                      </label>
                      <span className="text-[10px] text-slate-400 font-sans flex items-center">
                        <Key className="h-3 w-3 mr-1 text-slate-400" /> Cédula o clave personal
                      </span>
                    </div>
                    <input
                      type="password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full h-10 px-3.5 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-sans text-slate-900 placeholder-slate-400"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full h-11 bg-teal-600 hover:bg-teal-700 active:bg-teal-800 text-white font-sans text-xs font-black uppercase tracking-wider rounded-xl transition-all shadow-md hover:shadow-lg cursor-pointer mt-6 flex items-center justify-center space-x-2"
                >
                  <span>Ingresar como Colaborador</span>
                  <ArrowRight className="h-4 w-4" />
                </button>
              </form>
            )}

            {/* VIEW: Corporate Enterprise Portal Log In */}
            {mode === 'empresa_login' && (
              <form onSubmit={handleCompanyLogin} className="space-y-4" id="form-empresa-login">
                <div className="pb-2">
                  <span className="text-xs font-bold font-sans text-slate-800 flex items-center gap-1.5 p-1 bg-slate-50 rounded-lg inline-flex mb-2">
                    <Building className="h-3 w-3 text-indigo-600" />
                    Corporativo Quality SaaS
                  </span>
                  <p className="text-[11px] text-slate-400">
                    Ingrese el correo electrónico corporativo registrado de su empresa para administrar lotes, planes y accesos de personal.
                  </p>
                </div>

                <div className="space-y-3.5">
                  <div>
                    <label className="block text-[10px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">
                      Correo Electrónico de la Empresa
                    </label>
                    <input
                      type="email"
                      required
                      value={companyEmail}
                      onChange={(e) => setCompanyEmail(e.target.value)}
                      placeholder="ejemplo@miempresa.com"
                      className="w-full h-10 px-3.5 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-sans text-slate-900 placeholder-slate-400"
                    />
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-1.5">
                      <label className="block text-[10px] font-bold text-slate-600 uppercase tracking-wider">
                        Contraseña Corporativa
                      </label>
                      <button
                        type="button"
                        onClick={() => { setMode('password_recovery'); setRecoveryStep('request'); cleanFormErrors(); }}
                        className="text-[10px] text-indigo-600 hover:text-indigo-800 hover:underline font-bold font-sans cursor-pointer focus:outline-hidden"
                      >
                        ¿Olvidó su contraseña?
                      </button>
                    </div>
                    <input
                      type="password"
                      required
                      value={companyPassword}
                      onChange={(e) => setCompanyPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full h-10 px-3.5 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-sans text-slate-900 placeholder-slate-400"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full h-11 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white font-sans text-xs font-black uppercase tracking-wider rounded-xl transition-all shadow-md hover:shadow-lg cursor-pointer mt-6 flex items-center justify-center space-x-2"
                >
                  <span>Ingresar al Portal Corporativo</span>
                  <ArrowRight className="h-4 w-4" />
                </button>
              </form>
            )}

            {/* VIEW: SaaS Corporate Registration (15 Days Free Trial) */}
            {mode === 'empresa_registro' && !verificationStep && (
              <form onSubmit={handleRegisterCompanySubmit} className="space-y-4" id="form-empresa-registro">
                <div className="pb-1">
                  <span className="text-xs font-bold font-sans text-white flex items-center gap-1.5 p-1 px-2.5 bg-emerald-600 rounded-full inline-flex mb-2">
                    🚀 Prueba Gratis Activada
                  </span>
                  <p className="text-[11px] text-slate-400 font-sans">
                    Regístrese para iniciar su cuenta SaaS de control farmacéutico de forma inmediata con una <strong>Prueba Gratuita de 15 días</strong>.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] font-bold text-slate-655 uppercase tracking-wider mb-1">
                      Nombre de la Empresa *
                    </label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                      <input
                        type="text"
                        required
                        value={regNombre}
                        onChange={(e) => setRegNombre(e.target.value)}
                        placeholder=""
                        className="w-full h-10 pl-9 pr-3 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 font-sans"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-slate-650 uppercase tracking-wider mb-1">
                      NIT de la Empresa *
                    </label>
                    <div className="relative">
                      <Shield className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                      <input
                        type="text"
                        required
                        value={regNit}
                        onChange={(e) => setRegNit(e.target.value)}
                        placeholder=""
                        className="w-full h-10 pl-9 pr-3 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 font-sans"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-slate-650 uppercase tracking-wider mb-1">
                      Ciudad *
                    </label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                      <input
                        type="text"
                        required
                        value={regCiudad}
                        onChange={(e) => setRegCiudad(e.target.value)}
                        placeholder=""
                        className="w-full h-10 pl-9 pr-3 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 font-sans"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-slate-650 uppercase tracking-wider mb-1">
                      País *
                    </label>
                    <div className="relative">
                      <Globe className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                      <input
                        type="text"
                        required
                        value={regPais}
                        onChange={(e) => setRegPais(e.target.value)}
                        placeholder=""
                        className="w-full h-10 pl-9 pr-3 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 font-sans"
                      />
                    </div>
                  </div>

                  <div className="col-span-1 sm:col-span-2">
                    <label className="block text-[10px] font-bold text-slate-655 uppercase tracking-wider mb-1">
                      Dirección de Planta *
                    </label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                      <input
                        type="text"
                        required
                        value={regDireccion}
                        onChange={(e) => setRegDireccion(e.target.value)}
                        placeholder=""
                        className="w-full h-10 pl-9 pr-3 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 font-sans"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-slate-650 uppercase tracking-wider mb-1">
                      Correo Corporativo (Usuario Admin) *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                      <input
                        type="email"
                        required
                        value={regCorreo}
                        onChange={(e) => setRegCorreo(e.target.value)}
                        placeholder=""
                        className="w-full h-10 pl-9 pr-3 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 font-sans"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-slate-650 uppercase tracking-wider mb-1">
                      Número de Contacto *
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                      <input
                        type="text"
                        required
                        value={regContacto}
                        onChange={(e) => setRegContacto(e.target.value)}
                        placeholder=""
                        className="w-full h-10 pl-9 pr-3 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 font-sans"
                      />
                    </div>
                  </div>

                  <div className="col-span-1 sm:col-span-2">
                    <label className="block text-[10px] font-bold text-slate-650 uppercase tracking-wider mb-1">
                      Nueva Contraseña de Administrador *
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                      <input
                        type="password"
                        required
                        value={regPassword}
                        onChange={(e) => setRegPassword(e.target.value)}
                        placeholder=""
                        className="w-full h-10 pl-9 pr-3 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 font-sans"
                      />
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full h-11 bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 text-white font-sans text-xs font-black uppercase tracking-wider rounded-xl transition-all shadow-md hover:shadow-lg cursor-pointer mt-6 flex items-center justify-center space-x-2"
                >
                  <span>Iniciar Prueba Gratuita (15 Días)</span>
                  <ArrowRight className="h-4 w-4" />
                </button>
              </form>
            )}

            {/* Verification code sub-step screen */}
            {verificationStep && (
              <form onSubmit={handleVerifyEmailPIN} className="space-y-4" id="form-verify-pin">
                <div className="text-center p-3 bg-teal-50 border border-teal-150 rounded-xl space-y-1">
                  <span className="text-xs font-bold text-teal-800 font-sans">✉️ Verificación de Correo Corporativo</span>
                  <p className="text-[11px] text-slate-500 leading-relaxed font-sans">
                    Como parte de nuestro protocolo de seguridad FDA/INVIMA, hemos simulando enviar un token al correo corporativo indicado. Código Generado para pruebas rápidas es:
                  </p>
                  <span className="inline-block p-1 px-3 bg-teal-600 text-white font-mono text-sm tracking-wider font-extrabold rounded-lg select-text">{sentCode}</span>
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-slate-600 uppercase tracking-wider mb-1.5 text-center">
                    Ingrese el PIN de 6 dígitos
                  </label>
                  <input
                    type="text"
                    required
                    maxLength={6}
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="Escriba PIN ej: 345672"
                    className="w-full h-11 px-3 py-2 text-center text-sm font-mono tracking-widest bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:outline-hidden"
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setVerificationStep(false)}
                    className="flex-1 py-2 text-xs bg-slate-100 hover:bg-slate-200 text-slate-705 rounded-lg font-sans font-bold cursor-pointer"
                  >
                    Regresar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-xs font-sans font-black uppercase tracking-wider cursor-pointer shadow-xs"
                  >
                    Confirmar Código
                  </button>
                </div>
              </form>
            )}

            {/* VIEW: Password Recovery Module */}
            {mode === 'password_recovery' && (
              <div className="space-y-4">
                <div className="pb-1 border-b border-slate-100 flex justify-between items-center">
                  <h3 className="font-sans font-black text-slate-900 text-xs uppercase flex items-center gap-1.5">
                    <KeyRound className="h-4 w-4 text-orange-500" />
                    Restablecer Clave Corporativa
                  </h3>
                  <button
                    onClick={() => { setMode('empresa_login'); setRecoveryStep('request'); cleanFormErrors(); }}
                    className="text-[11px] text-slate-400 hover:text-slate-650 font-bold"
                  >
                    Volver al login
                  </button>
                </div>

                {recoveryStep === 'request' && (
                  <form onSubmit={handleRecoverySubmit} className="space-y-4" id="form-recovery-req">
                    <p className="text-[11px] text-slate-500 font-sans leading-relaxed">
                      Ingrese el <strong>NIT</strong> y el <strong>Correo de Contacto</strong> con el que registró su empresa para autenticar su derecho a clave de seguridad.
                    </p>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">
                        NIT de la Empresa
                      </label>
                      <input
                        type="text"
                        required
                        value={recoveryNit}
                        onChange={(e) => setRecoveryNit(e.target.value)}
                        placeholder="Ej. 901234567-8"
                        className="w-full h-10 px-3.5 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 font-sans"
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">
                        Correo Corporativo Registrado
                      </label>
                      <input
                        type="email"
                        required
                        value={recoveryEmail}
                        onChange={(e) => setRecoveryEmail(e.target.value)}
                        placeholder="gerencia@quality.com"
                        className="w-full h-10 px-3.5 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 font-sans"
                      />
                    </div>

                    <button
                      type="submit"
                      className="w-full h-10 bg-indigo-600 hover:bg-indigo-700 text-white font-sans text-xs font-bold rounded-xl transition-all shadow-md cursor-pointer uppercase tracking-wider"
                    >
                      Enviar PIN de Recuperación
                    </button>
                  </form>
                )}

                {recoveryStep === 'verify' && (
                  <form onSubmit={handleVerifyRecoveryCode} className="space-y-4" id="form-recovery-ver">
                    <p className="text-[11px] text-slate-500 font-sans leading-relaxed text-center">
                      PIN generado para restablecimiento rápido: <strong className="font-mono text-emerald-700 select-text p-1 bg-emerald-50 rounded-sm">{userSentCode}</strong>
                    </p>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-600 uppercase tracking-wider mb-1.5 text-center">
                        Ingrese PIN de 6 dígitos
                      </label>
                      <input
                        type="text"
                        required
                        maxLength={6}
                        value={recoveryCode}
                        onChange={(e) => setRecoveryCode(e.target.value.replace(/\D/g, ''))}
                        className="w-full h-11 text-center font-mono text-sm tracking-widest bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
                        placeholder="123456"
                      />
                    </div>

                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setRecoveryStep('request')}
                        className="flex-1 py-2 bg-slate-100 text-slate-600 rounded-lg text-xs"
                      >
                        Atrás
                      </button>
                      <button
                        type="submit"
                        className="flex-1 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold text-xs"
                      >
                        Verificar Código
                      </button>
                    </div>
                  </form>
                )}

                {recoveryStep === 'reset' && (
                  <form onSubmit={handleResetPassword} className="space-y-4" id="form-recovery-reset">
                    <p className="text-[11px] text-slate-500 font-sans leading-relaxed">
                      ¡Código verificado! Por favor digite e instrumente la nueva contraseña regulada para su empresa.
                    </p>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">
                        Nueva Contraseña Corporativa
                      </label>
                      <input
                        type="password"
                        required
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Minimo 5 caracteres"
                        className="w-full h-10 px-3.5 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>

                    <button
                      type="submit"
                      className="w-full h-10 bg-teal-600 hover:bg-teal-700 text-white font-sans text-xs font-bold rounded-xl transition-all shadow-md cursor-pointer uppercase tracking-wider"
                    >
                      Actualizar Contraseña y Acceder
                    </button>
                  </form>
                )}
              </div>
            )}

          </div>

          {/* Clean decorative badges footer */}
          <div className="flex justify-center items-center space-x-2 text-[10px] text-slate-400 font-mono">
            <span>🛡️ SSL Enforzado</span>
            <span>•</span>
            <span>📋 Resguardo Auditado</span>
            <span>•</span>
            <span>⚡ Multi-Tenant Active</span>
          </div>

        </div>
      </div>

      {/* Corporate footer */}
      <footer className="py-6 border-t border-slate-200 bg-slate-100/60">
        <div className="max-w-7xl mx-auto px-4 text-center space-y-1">
          <p className="text-xs text-slate-700 font-sans font-bold">
            Plataforma SaaS de Auditoría Técnica — Desarrollado por Juan Pablo Valencia Berrio
          </p>
          <p className="text-xs text-slate-500 font-sans">
            © 2026 Quality Procesos y Servicios S.A.S. Todos los derechos reservados.
          </p>
        </div>
      </footer>

      {/* Mobile App Install Modal */}
      <MobileInstallModal 
        isOpen={showMobileModal}
        onClose={() => setShowMobileModal(false)}
      />
    </div>
  );
}
